export const CHECKOUT_URL = "https://lipocase-en.impultienda.ar/checkout/";

export const images = {
  product: "https://cdn.magicpatterns.com/uploads/m65iecd5byfBWPBr7FF1Q4/LIPOCASE-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/7WwwpLXyUiHJXj4HaSLhxX/MICHAEL_CARTER_-_IMPLANTES.png",
};

export const reviewQuestions = [
  "What happened?",
  "What was the original objective?",
  "What do I actually observe now?",
  "What information is missing?",
  "Which concerns deserve further review?",
  "What questions should be clarified?",
  "What can I learn from this case?",
];

export const learningSources = [
  "You can read papers.",
  "You can watch surgical demonstrations.",
  "You can attend conferences.",
  "You can review before-and-after photographs.",
  "You can study anatomy.",
  "You can review previous cases.",
];

export const startQuestions = [
  "Where do I start?",
  "What should I review first?",
  "What information matters most?",
  "What should I document?",
  "What should I investigate further?",
];

export const beforeItems = [
  "You open a revision case and don’t know where to begin.",
  "You jump between photographs, notes, previous documentation and references.",
  "The original patient objective gets mixed together with the current situation.",
  "You focus on the visible result without systematically documenting the context.",
  "Important questions remain in your head instead of being written down.",
  "Every revision case feels like a completely different problem.",
  "You finish reviewing the case without a structured record of what you learned.",
];

export const afterItems = [
  "You begin every revision case with the same structured sequence.",
  "You reconstruct the original case context before analyzing the current situation.",
  "You separate what was expected, what was documented and what you observe now.",
  "You identify information gaps and questions that deserve further investigation.",
  "You organize observations instead of relying on scattered notes.",
  "You compare the original objective with the current case context.",
  "You finish each case with documented learning points you can return to later.",
];

export type SituationIcon = "alert" | "clock" | "help" | "camera" | "search" | "brain";

export const situations: { icon: SituationIcon; text: string }[] = [
  { icon: "alert", text: "You receive a patient presenting with a liposuction result that requires further review, and you’re not sure how to organize the case information first." },
  { icon: "clock", text: "You spend too much time going back through old notes, photographs, references and previous documentation." },
  { icon: "help", text: "You find yourself asking what the original objective actually was compared with what you’re seeing now." },
  { icon: "camera", text: "You have photographs and documentation, but they aren’t organized in a consistent framework that makes case review easier." },
  { icon: "search", text: "You notice something that deserves further investigation, but you don’t have a structured way to document the question." },
  { icon: "brain", text: "After reviewing a difficult case, you know there was something to learn from it — but you don’t have a system for capturing that learning." },
];

export const frameworkStages = [
  { name: "Reconstruct", q: "What was the original context and objective?" },
  { name: "Observe", q: "What do you actually see in the current case?" },
  { name: "Compare", q: "What appears different between the original objective and the current situation?" },
  { name: "Identify", q: "Which observations deserve further attention?" },
  { name: "Question", q: "What information remains unclear or requires additional investigation?" },
  { name: "Learn", q: "What can this case teach you for future case review?" },
];

export const volumes = [
  {
    n: "II",
    title: "The Revision Case Reconstruction Map™",
    lead: "Rebuild the story of the case before getting lost in the details.",
    intro: "A visual framework to organize:",
    items: ["Original patient objectives", "Relevant preoperative context", "Available documentation", "Visual documentation", "Previous case notes", "Current observations", "Patient-reported concerns", "Areas requiring clarification", "Information gaps", "Questions for further review"],
    outro: "Instead of looking at disconnected pieces of information, you reconstruct the case as a complete timeline.",
  },
  {
    n: "III",
    title: "The Lipo Revision Case Workbook™",
    lead: "The core workspace of the system.",
    intro: "A repeatable set of pages for documenting:",
    items: ["Case summary", "Original objectives", "Available documentation", "Current observations", "Patient-reported concerns", "Areas of attention", "Comparison notes", "Questions to investigate", "Information gaps", "Documentation review", "Learning points", "Future questions", "Case reflection"],
    outro: "Print it. Use it. Complete it. Review it. Then use the same structure on the next revision case.",
  },
  {
    n: "IV",
    title: "The Revision Case Comparison Atlas™",
    lead: "Stop reviewing revision cases in isolation.",
    intro: "Work through educational cases using the same comparison framework:",
    items: ["Original objective", "Available context", "Current observation", "Differences", "Questions", "Learning points"],
    flow: true,
    outro: "You’ll learn to compare cases using a consistent structure rather than relying on memory or scattered notes.",
  },
  {
    n: "V",
    title: "The Difficult Revision Case Atlas™",
    lead: "Some cases deserve a deeper review.",
    intro: "Structured educational scenarios designed to help you practice identifying:",
    items: ["What information stands out?", "What information is missing?", "What deserves further review?", "What questions should be clarified?", "What observations should be documented?", "What differences deserve attention?", "What assumptions should be questioned?", "What can be learned from the case?"],
    outro: "These are educational case-review exercises, not surgical protocols.",
  },
  {
    n: "VI",
    title: "The Professional Revision Learning System™",
    lead: "Turn every difficult case into a reusable learning asset.",
    intro: "Build your own professional case-review archive with templates for:",
    items: ["Case documentation", "Case reflection", "Case comparison", "Learning points", "Unanswered questions", "Knowledge-gap tracking", "Monthly case review", "Case progress tracking", "Pattern recognition notes", "Quarterly reflection", "Annual professional review"],
    outro: "Instead of simply finishing a difficult case… you start building your own professional revision-case library.",
  },
];

export type ToolIcon = "sheet" | "checklist" | "map" | "matrix" | "camera";

export const tools: { icon: ToolIcon; title: string; desc: string }[] = [
  { icon: "sheet", title: "The 15-Minute Revision Case Review Sheet™", desc: "A structured worksheet to organize an educational revision-case review from beginning to end." },
  { icon: "checklist", title: "The Revision Case Reconstruction Checklist", desc: "A checklist for organizing the information you want to review before analyzing the case." },
  { icon: "map", title: "The Revision Case Assessment Map", desc: "A visual framework for organizing observations, questions and information gaps." },
  { icon: "matrix", title: "The Case Comparison Matrix", desc: "Compare the original case context with the current situation using the same structure." },
  { icon: "camera", title: "The Revision Documentation Review Guide", desc: "A framework for organizing and reviewing available visual and case documentation." },
];

export const bonuses = [
  {
    n: 1,
    title: "30 Educational Revision Cases",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/9m9uKFsukZJndgTtxwVm1c/BONUS_1_LIPOCASE-EN.png",
    body: "A library of structured educational scenarios designed to help you repeatedly practice the revision-case framework. Each case follows the same sequence: Case → Reconstruct → Observe → Compare → Question → Learn.",
    punch: "Instead of reading another chapter, you get another opportunity to practice reviewing a difficult case.",
  },
  {
    n: 2,
    title: "The 10-Minute Revision Case Review™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/1PBPxLPdMWgvhytXJ9XKie/BONUS_2_LIPOCASE-EN.png",
    body: "Don’t have time for the complete review? Use the compact version of the framework. Perfect for quick case study, daily practice, case conferences, teaching sessions and short review periods.",
    punch: "10 minutes. One case. One structured review.",
  },
  {
    n: 3,
    title: "The Revision Case Error & Oversight Atlas™",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/cNUmPYRS12x8oJAydeLX2H/BONUS_3_LIPOCASE-EN.png",
    body: "Educational scenarios focused on case-review oversights: what information was missed, what deserved further review, what question could have been asked, what documentation would have helped and what lesson can be extracted.",
    punch: "Don’t simply look at what went wrong. Train yourself to look for what deserves attention.",
  },
  {
    n: 4,
    title: "The Professional Revision Case Review Cards™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/5autNoaqgrViHCzLJ2roun/BONUS_4_LIPOCASE-EN.png",
    body: "Printable case-review prompts for individual study, peer discussion, case conferences, teaching sessions and daily practice.",
    punch: "One card. One question. One case. One learning point.",
  },
  {
    n: 5,
    title: "The 30-Day Revision Case Review Challenge™",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/cAekSnjH8JQk71qUSp5guF/BONUS_5_LIPOCASE-EN.png",
    body: "A 30-day practice calendar designed to turn structured case review into a consistent learning habit. Each day: one case, one question, one observation, one reflection, one learning point.",
    punch: "In 30 days, you’ll have a structured record of your revision-case review practice.",
  },
];

export const testimonials = [
  {
    name: "Dr. Elena Ramírez",
    role: "Plastic Surgeon",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/868258f8-1ed9-4f69-b8c5-08347a9fec1f.jpg",
    quote: "What I value most is the sequence. I used to jump straight to the photographs. Now I reconstruct the original objective first, and my notes finally read like a coherent case.",
  },
  {
    name: "Dr. Richard Hale",
    role: "Aesthetic Physician",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/17465308-2e98-4be2-a5eb-3ae7e5f5b433.jpg",
    quote: "The comparison matrix changed how we run our monthly case conference. Everyone reviews with the same structure, so the discussion stays on observations and open questions.",
  },
  {
    name: "Dr. Mei Tanaka",
    role: "Aesthetic Physician",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/eac36190-440c-4244-8d35-745694cf4fc2.jpg",
    quote: "The learning-points template is the part I didn’t know I needed. After a difficult case I now have something written down I can actually return to.",
  },
];

export const feelBefore = [
  "You open a difficult revision case and don’t know where to begin.",
  "You jump between photographs, notes and references.",
  "You struggle to reconstruct the original objective.",
  "Important questions remain undocumented.",
  "You finish the review without capturing the lesson.",
];

export const feelAfter = [
  "You start with a defined review sequence.",
  "You reconstruct the original case context.",
  "You separate observations from questions.",
  "You compare the original objective with the current case.",
  "You document information gaps.",
  "You finish with structured learning points for future review.",
];

export const faqs = [
  { q: "How will I receive Lipo Revision Case Atlas™?", a: "Immediately after purchase, you’ll receive access to the digital materials. The six volumes, educational cases, worksheets, checklists and bonus resources are provided in downloadable digital formats." },
  { q: "Who is Lipo Revision Case Atlas™ for?", a: "It is designed for plastic surgeons and aesthetic physicians who want a structured educational framework for reviewing liposuction revision cases." },
  { q: "Is this a surgical technique course?", a: "No. Lipo Revision Case Atlas™ is an educational case-review and learning system. It is not a surgical technique course and does not provide individualized treatment instructions." },
  { q: "Does the Atlas provide a step-by-step surgical revision protocol?", a: "No. The system focuses on organizing case information, observations, questions, documentation and learning points. It does not replace clinical judgment, formal training, supervision, institutional protocols or current evidence." },
  { q: "How long does each case review take?", a: "The core framework is designed to help you structure a case review in approximately 15 minutes. More complex educational reviews may naturally require additional time." },
  { q: "Can I print the materials?", a: "Yes. The worksheets, checklists, maps and review cards are designed to be downloaded and printed for repeated educational use." },
  { q: "Is this a one-time payment?", a: "Yes. The launch price is $27 as a one-time payment with lifetime digital access, according to the stated purchase terms." },
  { q: "How long will I have access?", a: "You receive lifetime digital access to the purchased materials, subject to the applicable purchase terms." },
  { q: "What happens if I don’t like it?", a: "You have a 7-day guarantee. Review the materials and decide whether they fit your professional education and case-review process. Refund eligibility is subject to the stated guarantee conditions." },
];

export const buyers = [
  { name: "Valentina R.", country: "Mexico" },
  { name: "Dr. Santiago M.", country: "Colombia" },
  { name: "Camila G.", country: "Argentina" },
  { name: "Dr. Mateo L.", country: "Spain" },
  { name: "Isabella F.", country: "Chile" },
  { name: "Dr. Andrés P.", country: "Peru" },
  { name: "Lucía H.", country: "Uruguay" },
  { name: "Dr. Diego C.", country: "Ecuador" },
  { name: "Sofía V.", country: "Costa Rica" },
  { name: "Dr. Javier T.", country: "Dominican Republic" },
  { name: "Mariana S.", country: "Panama" },
  { name: "Dr. Alejandro B.", country: "Guatemala" },
  { name: "Gabriela N.", country: "Paraguay" },
  { name: "Dr. Fernando O.", country: "Bolivia" },
  { name: "Daniela A.", country: "Puerto Rico" },
];
